import { useState, useEffect, useRef } from 'react'
import cutileiApi from 'services/cutileiApi'
import { logApiError } from 'services/apiError'
import viaCepApi from 'services/viaCepApi'
import MapPicker from 'components/MapPicker'
import CheckBox from 'components/CheckBox'
import MainButton from 'components/Buttons/MainButton'
import submitOnEnter from 'utils/submitOnEnter'
import addressValidator from 'validators/addressValidator'
import { ReactComponent as Loading } from 'icons/loading2.svg'
import { MdNavigateNext } from 'react-icons/md'
import { Formik } from 'formik'

import {
  Form,
  Row,
  CheckRow,
  Label,
  SubTitle,
  InfoText,
  FormField,
  WarningText,
  Input,
  MaskedInput,
  LoadingBackdrop,
  AddressContainer
} from '../styles'

export default function AddressForm ({
  businessData = {},
  onFormSubmitted: handleFormSubmitted = () => null
}) {
  const [loadingAddress, setLoadingAddress] = useState (false)
  const [loadingCoords, setLoadingCoords] = useState (false)
  const [addressFound, setAddressFound] = useState (false)
  const coordsFromMap = useRef (false)

  return (
    <Formik
      validationSchema={addressValidator}
      initialValues={businessData.address}
      onSubmit={values => handleFormSubmitted ({ address: values })}
    >
      {function SignUpAddressForm ({
        values,
        errors,
        touched,
        setFieldValue,
        handleChange,
        handleBlur,
        handleSubmit
      }) {
        const searchAddress = async event => {
          const postal_code = event.target.rawValue

          setFieldValue ('postal_code', postal_code)
          
          if (postal_code.length === 8) {
            setLoadingAddress (true)
            try {
              const response = await viaCepApi.get (`/ws/${postal_code}/json`)
              const { data } = response

              setFieldValue ('route', data.logradouro)
              setFieldValue ('district', data.bairro)
              setFieldValue ('city', data.localidade)
              setFieldValue ('state', data.uf)

              setAddressFound (true)
            } catch (error) {
              logApiError (error)
            } finally {
              setLoadingAddress (false)
            }
          }
        }

        const getCoordinates = async () => {
          try {
            setLoadingCoords (true)

            const { data: coordinates } = await cutileiApi.post ('/coordinates', values)

            setFieldValue ('latitude', coordinates.latitude)
            setFieldValue ('longitude', coordinates.longitude)
          } catch (error) {
            logApiError (error)
          } finally {
            setLoadingCoords (false)
          }
        }

        const handleLocationChange = async location => {
          try {
            setLoadingAddress (true)
            const { latitude, longitude } = location

            const { data: address } = await cutileiApi.get (
              `/coordinates?latitude=${latitude}&longitude=${longitude}`
            )
            
            coordsFromMap.current = true
            setFieldValue ('route', address.route)
            setFieldValue ('number', address.number)
            setFieldValue ('district', address.district)
            setFieldValue ('city', address.city)
            setFieldValue ('state', address.state)
            setFieldValue ('postal_code', address.postal_code)
            setFieldValue ('latitude', latitude)
            setFieldValue ('longitude', longitude)
          } catch (error) {
            logApiError (error)
          } finally {
            setLoadingAddress (false)
          }
        }

        const debounceTimeout = useRef ()

        useEffect (() => {
          if (
            addressFound && !coordsFromMap.current && values.route &&
            values.number && values.city && values.state
          ) {
            if (debounceTimeout.current) clearTimeout (debounceTimeout.current)
            debounceTimeout.current = setTimeout (() => {
              getCoordinates ()
              coordsFromMap.current = false
            }, 700)
          }
          return () => clearTimeout (debounceTimeout.current)
        }, [addressFound, values.route, values.number, values.city, values.state])

        return (
          <Form onKeyDown={submitOnEnter(handleSubmit)} style={{ width: 800 }}>
            <SubTitle>Endereço do salão</SubTitle>
            <AddressContainer>
              {loadingAddress && (
                <LoadingBackdrop>
                  <Loading/>
                </LoadingBackdrop>
              )}
              <Row>
                <FormField>
                  <MaskedInput
                    id='postal_code'
                    name='postal_code'
                    placeholder='CEP'
                    options={{
                      blocks: [5, 3],
                      delimiters: ['-'],
                      numericOnly: true
                    }}
                    value={values.postal_code}
                    onChange={searchAddress}
                    onBlur={handleBlur ('postal_code')}
                  />
                  {touched.postal_code && errors.postal_code && (
                    <WarningText>{errors.postal_code}</WarningText>
                  )}
                </FormField>
                <FormField stretch={2}>
                  <Input placeholder='Rua/avenida' value={values.route} disabled/>
                </FormField>
                <FormField>
                  <Input
                    placeholder='Número'
                    value={values.number}
                    onChange={handleChange ('number')}
                    onBlur={handleBlur ('number')}
                  />
                  {touched.number && errors.number && (
                    <WarningText>{errors.number}</WarningText>
                  )}
                </FormField>
                <FormField>
                  <Input
                    placeholder='Complemento'
                    value={values.complement}
                    onChange={handleChange ('complement')}
                    onBlur={handleBlur ('complement')}
                  />
                </FormField>
              </Row>
              <Row>
                <FormField stretch={2}>
                  <Input placeholder='Bairro' value={values.district} disabled/>
                </FormField>
                <FormField>
                  <Input placeholder='Cidade' value={values.city} disabled/>
                </FormField>
                <FormField>
                  <Input placeholder='Estado' value={values.state} disabled/>
                </FormField>
              </Row>
              <FormField>
                <SubTitle style={{marginBottom: 8}}>
                  Seu salão fica em um prédio comercial?
                </SubTitle>
                <Row style={{gap: 15}}>
                  <CheckRow>
                    <CheckBox
                      id='is_commercial_building'
                      value={values.is_commercial_building}
                      onValueChange={() => setFieldValue ('is_commercial_building', true)}
                    />
                    <Label htmlFor='is_commercial_building' style={{fontSize: 14}}>
                      Sim
                    </Label>
                  </CheckRow>
                  <CheckRow>
                    <CheckBox
                      id='!is_commercial_building'
                      value={!values.is_commercial_building}
                      onValueChange={() => setFieldValue ('is_commercial_building', false)}
                    />
                    <Label htmlFor='!is_commercial_building' style={{fontSize: 14}}>
                      Não
                    </Label>
                  </CheckRow>
                </Row>
              </FormField>
              {loadingCoords ? (
                <Row>
                  <Loading/>
                </Row>
              ) : values.latitude !== 0 && values.longitude !== 0 && (
                <FormField>
                  <SubTitle style={{marginBottom: 3}}>
                    {'Mantenha o cursor do mouse pressionado sobre o pino de localização '
                    + 'e o arraste para a localização exata do seu salão, se for necessário'}
                  </SubTitle>
                  <InfoText style={{marginBottom: 14}}>
                    {'Este ajuste é muito importante para que a Cutilei possa '
                    + 'verificar a localização dos clientes ao validar agendamentos'}
                  </InfoText>
                  <MapPicker
                    initialCoords={{
                      latitude: values.latitude,
                      longitude: values.longitude
                    }}
                    onLocationSelected={handleLocationChange}
                  />
                </FormField>
              )}
            </AddressContainer>
            <MainButton
              leftIcon={<MdNavigateNext size={30}/>}
              aria-label='Próximo'
              onClick={handleSubmit}
              width={60}
              style={{marginTop: 10}}
            />
          </Form>
        )
      }}
    </Formik>
  )
}
